import React, { useContext, useMemo, useState } from 'react';
import { ClearInputField } from '@td-client-frontend/icons';
import { TextField, DatePicker } from '@td-client-frontend/common';
import { GroupSearchSelect, ALL_OPTION } from '../../../components/GroupSearchSelect';
import { Group } from '../../../@types/Members';
import { SearchSelectItem } from '../../../../../common/src/components/form-controls';
import { Button } from '../../../components/Button';
import { format, isValid } from 'date-fns';
import { MemberSearchContext } from '../../../context/MemberSearchContext';

export interface MemberSearchResult {
  firstName: string;
  lastName: string;
  dob: string;
  group: string | number;
}

export interface MemberSearchProps {
  onChange: (searchResult: MemberSearchResult) => void;
}

export interface GroupItem {
  label: string;
  value: string | number;
}

const ClearTextButton = ({ onClick, isVisible }: { onClick: () => void; isVisible: boolean }) =>
  isVisible ? (
    <button className="flex items-center" onClick={onClick} title="Clear Text">
      <ClearInputField className="fill-current" aria-hidden />
    </button>
  ) : null;

const EFFICIENT_VALUE_LENGTH = 2;

export const MemberSearch = ({ onChange }: MemberSearchProps) => {
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [dob, setDob] = useState<string>('');
  const [selectedGroup, setSelectedGroup] = useState<GroupItem>(ALL_OPTION);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { tooManyRecords } = useContext(MemberSearchContext);

  const disableSearch = tooManyRecords && selectedGroup?.value === null;

  const clearButtonVisisble = useMemo(
    () => Boolean(firstName || lastName || dob || selectedGroup?.value || searchTerm),
    [firstName, lastName, searchTerm, selectedGroup, dob]
  );

  const handleSearch = () => {
    onChange({
      firstName,
      lastName,
      dob,
      group: selectedGroup.value
    });
  };

  const resetSearchState = () => {
    setFirstName('');
    setLastName('');
    setDob('');
    setSelectedGroup({ value: '', label: '' });
    setSearchTerm('');
  };

  return (
    <div className="flex gap-8 mt-5 items-center whitespace-nowrap">
      <TextField
        name="firstNameField"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
        value={firstName}
        placeholder="First Name"
        Right={
          <ClearTextButton
            onClick={() => {
              setFirstName('');
            }}
            isVisible={firstName.length >= EFFICIENT_VALUE_LENGTH}
          />
        }
        aria-label="First Name"
        label="First Name"
      />
      <TextField
        name="lastNameField"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
        value={lastName}
        placeholder="Last Name"
        Right={
          <ClearTextButton
            onClick={() => {
              setLastName('');
            }}
            isVisible={lastName.length >= EFFICIENT_VALUE_LENGTH}
          />
        }
        aria-label="Last Name"
        label="Last Name"
      />
      <DatePicker
        name="member_dob"
        label="Date of Birth"
        aria-label="Date of Birth"
        value={isValid(new Date(dob)) ? format(new Date(dob), 'MM/dd/yyyy') : ''}
        placeholder="MM/DD/YYYY"
        onBlur={(e) => {
          if (e.target.value === '') {
            setDob('');
          }
        }}
        onChange={(date: any) => setDob(date)}
        closeOnScroll
      />
      <GroupSearchSelect
        value={selectedGroup}
        requiredPerm="MemberRead"
        notTermed
        onSelect={(item: Group | SearchSelectItem) => {
          if ('id' in item) {
            setSelectedGroup({ value: item.id, label: item.name });
          } else {
            setSelectedGroup(item);
          }
        }}
        includeAll={false}
        onSearchChange={(val: any) => {
          if (!val) {
            setSelectedGroup(ALL_OPTION);
          } else {
            setSearchTerm(val);
          }
        }}
      />
      <Button
        onClick={handleSearch}
        id="member-search-button"
        tooltip={disableSearch ? 'Please enter search criteria' : ''}
        disabled={disableSearch}
      >
        Search
      </Button>
      {clearButtonVisisble && (
        <button
          onClick={resetSearchState}
          className="space-x-2 btn btn-default pb-4 w-auto"
          style={{ maxWidth: '70px' }}
        >
          <span className="text-primary-500 whitespace-no-wrap">Clear all</span>
        </button>
      )}
    </div>
  );
};
